import { useCallback, useMemo, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  LayoutGrid,
  Link2,
  List as ListIcon,
  Loader2,
  Plus,
  Search,
  UserRound,
  X,
} from "lucide-react";
import {
  Input,
  Table,
  TableBody,
  TableHeader,
  TableRow,
} from "@bookdental/ui";
import { keepPreviousData } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAdaptiveDebounce } from "@/lib/debounce";
import { useSetPageHeader } from "@/lib/page-header";
import { BotaoExportar } from "@/components/shared/BotaoExportar";
import { usePermissao } from "@/lib/use-permissao";
import { COLUNAS_PACIENTE } from "@/lib/colunas-export";
import type { CampoDaExportacaoDePacientes } from "@bookdental/schemas";
import type { ColunaExport } from "@/lib/exportar-tabela";
import { useConfirm } from "@/lib/confirm";
import { useAuth } from "@/lib/auth";
import { useIsMobile } from "@/lib/use-media-query";
import { PatientListRow, type PatientRowShape } from "@/components/patients/PatientListRow";
import { PatientListCard } from "@/components/patients/PatientListCard";
import { PatientGridCard } from "@/components/patients/PatientGridCard";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { PatientInviteLinkDialog } from "@/components/patients/PatientInviteLinkDialog";
import { PacientesDoAcessoPessoal } from "@/components/patients/pessoal/PacientesDoAcessoPessoal";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { PACIENTES_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { cn } from "@/lib/cn";

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;
const LS_KEY_PAGE_SIZE = "bd:patients:pageSize";
const LS_KEY_SORT = "bd:patients:sort";

type SortBy = "name" | "createdAt";
type SortOrder = "asc" | "desc";

/** No acesso pessoal de quem também está em outras organizações, a lista junta todas (com filtro);
 *  fora dele, a lista da organização ativa. */
export function Patients() {
  const { user } = useAuth();
  const pessoal = !!user?.activeOrganization?.isPersonal;
  const minhas = trpc.organization.listMine.useQuery(undefined, { enabled: pessoal, staleTime: 60_000 });
  const temOutras = (minhas.data ?? []).some((m) => !m.organization.isPersonal);
  if (pessoal && temOutras) return <PacientesDoAcessoPessoal />;
  return <PacientesDaOrganizacao />;
}

function PacientesDaOrganizacao() {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const [searchInput, setSearchInput] = useState("");
  const searchDebounced = useAdaptiveDebounce(searchInput);
  const [active, setActive] = useState<"all" | "active" | "inactive">("active");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(() => {
    const stored = typeof window !== "undefined" ? Number(localStorage.getItem(LS_KEY_PAGE_SIZE)) : 0;
    return PAGE_SIZE_OPTIONS.includes(stored as 25) ? stored : 50;
  });
  const [sort, setSort] = useState<{ by: SortBy; order: SortOrder }>(() => {
    if (typeof window === "undefined") return { by: "name", order: "asc" };
    try {
      const raw = localStorage.getItem(LS_KEY_SORT);
      if (raw) {
        const parsed = JSON.parse(raw);
        if ((parsed.by === "name" || parsed.by === "createdAt") && (parsed.order === "asc" || parsed.order === "desc")) {
          return parsed;
        }
      }
    } catch {
      // ignore
    }
    return { by: "name", order: "asc" };
  });
  // No celular a lista cresce por "Ver mais" em vez de paginar: rolar é o gesto natural do toque,
  // e o rodapé de paginação come altura útil. O desktop segue paginado — pular pra uma página
  // distante continua fazendo sentido com mouse. `md` é o mesmo limiar dos cartões.
  const isMobile = useIsMobile();
  const [carregados, setCarregados] = useState<number>(PAGE_SIZE_OPTIONS[1]);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  /** Lista (tabela) ou cartões em grade — preferência por navegador, como as larguras da
   *  planilha de produção. No celular a lista já é de cartões, então o alternador é só desktop. */
  const [layout, setLayout] = useState<"lista" | "cards">(() => {
    if (typeof window === "undefined") return "lista";
    return window.localStorage.getItem("bookdental.pacientes.layout") === "cards" ? "cards" : "lista";
  });
  const trocarLayout = (v: "lista" | "cards") => {
    setLayout(v);
    window.localStorage.setItem("bookdental.pacientes.layout", v);
  };
  const [inviteLinkDialogOpen, setInviteLinkDialogOpen] = useState(false);

  const listQuery = trpc.patient.list.useQuery(
    {
      search: searchDebounced.trim() || undefined,
      active: active === "all" ? undefined : active === "active",
      sortBy: sort.by,
      order: sort.order,
      take: isMobile ? carregados : pageSize,
      skip: isMobile ? 0 : page * pageSize,
    },
    { placeholderData: keepPreviousData },
  );

  // Exporta a CONSULTA, não a página: mesma busca, mesmo filtro de ativos, mesma ordenação. Quem
  // busca é o servidor (`patient.exportar`), com permissão própria, só os campos das colunas
  // marcadas e a auditoria sempre gravada.
  const podeExportar = usePermissao("patients.export");
  const { mutateAsync: exportarPacientes } = trpc.patient.exportar.useMutation();
  const buscarPacientesParaExportar = useCallback(
    async (colunas: ColunaExport<PatientRowShape>[]) => {
      const r = await exportarPacientes({
        search: searchDebounced.trim() || undefined,
        active: active === "all" ? undefined : active === "active",
        sortBy: sort.by,
        order: sort.order,
        campos: colunas.map((c) => c.campo as CampoDaExportacaoDePacientes),
      });
      if (r.truncada) toast.warning(`A planilha saiu com os primeiros ${r.patients.length} pacientes. Use a busca ou os filtros para exportar o resto.`);
      return r.patients as PatientRowShape[];
    },
    [exportarPacientes, searchDebounced, active, sort.by, sort.order],
  );

  const handleSort = (by: SortBy) => {
    setSort((prev) => {
      const next: { by: SortBy; order: SortOrder } =
        prev.by === by
          ? { by, order: prev.order === "asc" ? "desc" : "asc" }
          : { by, order: by === "createdAt" ? "desc" : "asc" };
      if (typeof window !== "undefined") localStorage.setItem(LS_KEY_SORT, JSON.stringify(next));
      return next;
    });
    setPage(0);
  };

  const remove = trpc.patient.remove.useMutation({
    onSuccess: () => {
      toast.success("Paciente excluído");
      utils.patient.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Falha ao excluir"),
  });

  const activate = trpc.patient.activate.useMutation({
    onSuccess: () => {
      toast.success("Paciente reativado");
      utils.patient.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Falha ao reativar"),
  });

  const deactivate = trpc.patient.deactivate.useMutation({
    onSuccess: () => {
      toast.success("Paciente inativado");
      utils.patient.list.invalidate();
    },
    onError: (e) => toast.error(e.message || "Falha ao inativar"),
  });

  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const patients = useMemo(
    () => (listQuery.data?.patients ?? []) as PatientRowShape[],
    [listQuery.data],
  );

  useTourDaPagina(PACIENTES_TOUR_STEPS);
  // Registra header no Topbar (apenas título + contagem).
  useSetPageHeader(
    {
      title: "Pacientes",
      subtitle: listQuery.isLoading
        ? "Carregando..."
        : `${total} paciente${total === 1 ? "" : "s"} cadastrado${total === 1 ? "" : "s"}`,
    },
    [total, listQuery.isLoading],
  );

  const handleRemove = async (p: PatientRowShape) => {
    const ok = await confirm({
      title: `Excluir ${p.name}?`,
      description:
        "Essa ação é permanente. Todos os dados, consultas e anexos do paciente serão removidos.",
      confirmText: "Excluir paciente",
      cancelText: "Cancelar",
      destructive: true,
    });
    if (ok) remove.mutate({ id: p.id });
  };

  const handleToggleActive = (p: PatientRowShape) => {
    if (p.active) deactivate.mutate({ id: p.id });
    else activate.mutate({ id: p.id });
  };

  const handlePageSize = (n: number) => {
    setPageSize(n);
    setPage(0);
    if (typeof window !== "undefined") localStorage.setItem(LS_KEY_PAGE_SIZE, String(n));
  };

  const from = total === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);

  const isSearching = listQuery.isFetching && searchInput.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-4">
      {/* Barra de filtros */}
      <div className="flex flex-wrap items-center gap-3">
        {/* No celular o Exportar vira os três pontos ao lado da busca — ação rara não ganha botão
            de linha inteira numa tela de 360px. */}
        <div className="flex w-full min-w-0 items-center gap-2 sm:max-w-md sm:flex-1">
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <Input
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPage(0);
              }}
              placeholder="Buscar pacientes..."
              className="h-11 pl-9 pr-9 text-base sm:h-9 sm:text-sm"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              {isSearching ? (
                <Loader2 className="h-4 w-4 animate-spin text-brand-600" />
              ) : searchInput ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    setPage(0);
                  }}
                  className="flex h-11 w-11 items-center justify-center text-zinc-400 hover:text-zinc-700 sm:h-auto sm:w-auto"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </span>
          </div>
          {isMobile && podeExportar && (
            <BotaoExportar
              gatilho="tresPontos"
              nome="pacientes"
              titulo="pacientes"
              reimportavel
              quantidade={listQuery.data?.total}
              colunas={COLUNAS_PACIENTE}
              idDe={(p) => p.id}
              buscarLinhas={buscarPacientesParaExportar}
            />
          )}
        </div>
        <div data-tour="pacientes-filtro-status" className="flex w-full items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 sm:inline-flex sm:w-auto">
          {(["active", "inactive", "all"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => {
                setActive(k);
                setPage(0);
              }}
              className={
                active === k
                  ? "inline-flex h-11 flex-1 items-center justify-center rounded-md bg-brand-600 px-2.5 text-[12.5px] font-semibold text-white shadow-sm transition-colors sm:h-8 sm:flex-none"
                  : "inline-flex h-11 flex-1 items-center justify-center rounded-md px-2.5 text-[12.5px] font-semibold text-zinc-600 transition-colors hover:text-zinc-900 sm:h-8 sm:flex-none"
              }
            >
              {k === "active" ? "Ativos" : k === "inactive" ? "Inativos" : "Todos"}
            </button>
          ))}
        </div>
        {/* Lista x cartões — só no desktop: no celular a listagem já é cartão. */}
        <div className="hidden items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 md:inline-flex dark:border-border dark:bg-muted/40">
          {([
            { key: "lista", rotulo: "Lista", Icone: ListIcon },
            { key: "cards", rotulo: "Cartões", Icone: LayoutGrid },
          ] as const).map(({ key, rotulo, Icone }) => (
            <button
              key={key}
              type="button"
              onClick={() => trocarLayout(key)}
              title={rotulo}
              aria-label={rotulo}
              aria-pressed={layout === key}
              className={
                layout === key
                  ? "inline-flex h-8 items-center gap-1.5 rounded-md bg-brand-600 px-2.5 text-[12.5px] font-semibold text-white shadow-sm"
                  : "inline-flex h-8 items-center gap-1.5 rounded-md px-2.5 text-[12.5px] font-semibold text-zinc-600 transition-colors hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground"
              }
            >
              <Icone className="h-3.5 w-3.5" />
              {rotulo}
            </button>
          ))}
        </div>

        {/* `w-full` no celular: as ações ganham linha própria, e com isso o botão cabe escrito por
            extenso ("Novo paciente") em vez de encolher pra "Novo". */}
        <div className="ml-auto flex w-full flex-wrap items-center justify-end gap-1.5 sm:w-auto sm:flex-nowrap">
          {!isMobile && podeExportar && (
            <BotaoExportar
              nome="pacientes"
              titulo="pacientes"
              reimportavel
              quantidade={listQuery.data?.total}
              colunas={COLUNAS_PACIENTE}
              idDe={(p) => p.id}
              buscarLinhas={buscarPacientesParaExportar}
              className="h-11 w-full shrink-0 justify-center bg-white font-semibold shadow-sm sm:h-9 sm:w-auto dark:bg-background"
            />
          )}
          <button
            type="button"
            data-tour="pacientes-gerar-link"
            onClick={() => setInviteLinkDialogOpen(true)}
            className="inline-flex h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-[13px] font-semibold text-zinc-700 shadow-sm transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 sm:h-9 dark:border-border dark:bg-background dark:text-foreground dark:hover:border-primary/40 dark:hover:bg-primary/10 dark:hover:text-primary"
            title="Gerar link de cadastro para o paciente preencher"
            aria-label="Gerar link de cadastro para o paciente preencher"
          >
            <Link2 className="h-4 w-4" />
            <span>Gerar link</span>
          </button>
          <button
            type="button"
            data-tour="pacientes-novo"
            onClick={() => setNewDialogOpen(true)}
            className="inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-1 sm:h-9 sm:flex-none"
          >
            <Plus className="h-4 w-4" />
            Novo paciente
          </button>
        </div>
      </div>

      {/* Tabela. Na visão em cartões a moldura sai a partir de `md`: ela é a casca da TABELA, e em
          volta de uma grade virava uma borda solta com um vão depois do último cartão. No celular
          continua valendo — lá a lista é a `ul` de cartões, que segue dentro dela. */}
      <div
        className={cn(
          "overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card",
          layout === "cards" &&
            "md:rounded-none md:border-0 md:bg-transparent md:shadow-none dark:md:bg-transparent",
        )}
      >
        {listQuery.isLoading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
          </div>
        ) : total === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-zinc-100">
              <UserRound className="h-8 w-8 text-zinc-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-zinc-700">
                {searchDebounced ? "Nenhum paciente encontrado" : "Nenhum paciente cadastrado"}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {searchDebounced
                  ? "Tente outro termo ou altere o filtro."
                  : "Comece adicionando seu primeiro paciente."}
              </p>
            </div>
            {!searchDebounced && (
              <button
                type="button"
                onClick={() => setNewDialogOpen(true)}
                className="mt-2 inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-xs font-medium text-white hover:bg-brand-700"
              >
                <Plus className="h-3.5 w-3.5" />
                Adicionar paciente
              </button>
            )}
          </div>
        ) : (
          <>
          {/* Celular: cartões, com as ações no menu em vez de quatro ícones de 28px na borda.
              A tabela abaixo segue idêntica no desktop. */}
          <ul className="divide-y divide-zinc-100 md:hidden dark:divide-border">
            {patients.map((p) => (
              <PatientListCard
                key={p.id}
                patient={p}
                onRemove={handleRemove}
                onToggleActive={handleToggleActive}
              />
            ))}
          </ul>

          {layout === "cards" ? (
            // Duas colunas, e só duas: com três o cartão cai pra ~380px e vira a lista de novo,
            // empilhada. `items-stretch` + `h-full` no cartão fazem a linha inteira ter a altura do
            // cartão mais alto, em vez de cada um encolher no seu conteúdo.
            <div className="hidden items-stretch gap-4 md:grid md:grid-cols-2">
              {patients.map((p) => (
                <PatientGridCard
                  key={p.id}
                  patient={p}
                  onRemove={handleRemove}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </div>
          ) : (
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow className="bg-zinc-50/60 hover:bg-zinc-50/60">
                <Th hidden="sm" width="40px" center>#</Th>
                <Th width="40px" center>Status</Th>
                <Th>
                  <SortButton label="Nome" active={sort.by === "name"} order={sort.order} onClick={() => handleSort("name")} />
                </Th>
                <Th hidden="sm" center>CPF</Th>
                <Th hidden="md" center>E-mail</Th>
                <Th hidden="md" center>Telefone</Th>
                <Th hidden="lg" center>Nascimento</Th>
                <Th hidden="lg" center>Tags</Th>
                <Th hidden="xl" center>
                  <SortButton label="Cadastro" active={sort.by === "createdAt"} order={sort.order} onClick={() => handleSort("createdAt")} />
                </Th>
                <Th center width="112px">Ações</Th>
              </TableRow>
            </TableHeader>
            <TableBody>
              {patients.map((p, idx) => (
                <PatientListRow
                  key={p.id}
                  patient={p}
                  index={page * pageSize + idx}
                  onRemove={handleRemove}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </TableBody>
          </Table>
          )}
          </>
        )}
      </div>

      {/* Celular: "Ver mais" acumula. Some quando tudo já está na tela. */}
      {total > 0 && isMobile && (
        <div className="space-y-2 md:hidden">
          {patients.length < total && (
            <button
              type="button"
              onClick={() => setCarregados((n) => n + pageSize)}
              disabled={listQuery.isFetching}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-zinc-200 bg-white text-[13px] font-semibold text-brand-700 transition-colors hover:border-brand-300 hover:bg-brand-50 disabled:opacity-60 dark:border-border dark:bg-card dark:text-primary dark:hover:bg-primary/10"
            >
              {listQuery.isFetching && <Loader2 className="h-4 w-4 animate-spin" />}
              Ver mais
            </button>
          )}
          <p className="text-center text-xs text-zinc-500 dark:text-muted-foreground">
            {patients.length} de {total} paciente{total === 1 ? "" : "s"}
          </p>
        </div>
      )}

      {/* Desktop: paginação com números e itens por página, como sempre foi. */}
      {total > 0 && !isMobile && (
        <div className="hidden flex-wrap items-center justify-between gap-x-3 gap-y-2 text-sm md:flex">
          <div className="text-xs text-zinc-500">
            {from}–{to} de {total}
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
          />
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <span>Por página:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSize(Number(e.target.value))}
              className="h-11 rounded-md border border-zinc-200 bg-white px-2 text-base text-zinc-700 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:h-8 sm:text-xs"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <PatientFormDialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)} />
      <PatientInviteLinkDialog
        open={inviteLinkDialogOpen}
        onClose={() => setInviteLinkDialogOpen(false)}
      />
    </div>
  );
}

function SortButton({
  label,
  active,
  order,
  onClick,
}: {
  label: string;
  active: boolean;
  order: SortOrder;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded hover:text-zinc-900 ${active ? "text-brand-700" : ""}`}
    >
      {label}
      {active ? (
        order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-40" />
      )}
    </button>
  );
}

function Th({
  children,
  center,
  right,
  hidden,
  width,
}: {
  children: React.ReactNode;
  center?: boolean;
  right?: boolean;
  hidden?: "sm" | "md" | "lg" | "xl";
  width?: string;
}) {
  const hiddenCls = hidden === "sm"
    ? "hidden sm:table-cell"
    : hidden === "md"
      ? "hidden md:table-cell"
      : hidden === "lg"
        ? "hidden lg:table-cell"
        : hidden === "xl"
          ? "hidden xl:table-cell"
          : "";
  return (
    <th
      className={`h-9 px-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${
        center ? "text-center" : right ? "text-right" : "text-left"
      } ${hiddenCls}`}
      style={width ? { width } : undefined}
    >
      {children}
    </th>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;

  const current = page + 1;
  const pages: (number | "…")[] = [];
  const add = (n: number | "…") => pages.push(n);

  const maxAround = 1;
  add(1);
  if (current - maxAround > 2) add("…");
  for (let i = Math.max(2, current - maxAround); i <= Math.min(totalPages - 1, current + maxAround); i++) {
    add(i);
  }
  if (current + maxAround < totalPages - 1) add("…");
  if (totalPages > 1) add(totalPages);

  const btn = "inline-flex h-8 min-w-[32px] items-center justify-center rounded-md px-2 text-xs transition-colors";

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => onChange(0)}
        disabled={page === 0}
        className={`${btn} text-zinc-500 hover:bg-zinc-100 disabled:opacity-40`}
      >
        <ChevronsLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(Math.max(0, page - 1))}
        disabled={page === 0}
        className={`${btn} text-zinc-500 hover:bg-zinc-100 disabled:opacity-40`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`e${i}`} className="px-1 text-xs text-zinc-400">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => onChange(p - 1)}
            className={`${btn} ${
              p === current
                ? "bg-brand-600 font-semibold text-white hover:bg-brand-600"
                : "text-zinc-600 hover:bg-zinc-100"
            }`}
          >
            {p}
          </button>
        ),
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(totalPages - 1, page + 1))}
        disabled={page + 1 >= totalPages}
        className={`${btn} text-zinc-500 hover:bg-zinc-100 disabled:opacity-40`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onChange(totalPages - 1)}
        disabled={page + 1 >= totalPages}
        className={`${btn} text-zinc-500 hover:bg-zinc-100 disabled:opacity-40`}
      >
        <ChevronsRight className="h-4 w-4" />
      </button>
    </div>
  );
}
